import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Divider,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import api from "api/axios";

export const AFFILIATIONS = ["Standard", "Assaya/Evalyn"];
const ASSAYA = "Assaya/Evalyn";

/**
 * A doctor's organisation: the affiliation and, for Assaya/Evalyn, the PHC
 * name. Only the admin sets it (PATCH /admin/doctor/:id/organisation); the
 * doctor's web profile shows it read-only. It shapes the doctor's and their
 * staff's app (menu, stats, registration, prescriptions) from their next
 * sign-in or reload. Switching to Standard keeps the stored PHC name.
 */
const DoctorOrganisationCard = ({ doctor, onSaved }) => {
  const storedAffiliation = doctor.affiliation || "Standard";
  const storedPhcName = doctor.phcName || "";
  const [affiliation, setAffiliation] = useState(storedAffiliation);
  const [phcName, setPhcName] = useState(storedPhcName);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setAffiliation(storedAffiliation);
    setPhcName(storedPhcName);
  }, [storedAffiliation, storedPhcName]);

  const isAssaya = affiliation === ASSAYA;
  const changed =
    affiliation !== storedAffiliation ||
    (isAssaya && phcName.trim() !== storedPhcName);

  const save = async () => {
    setSaving(true);
    try {
      const response = await api.patch(`/admin/doctor/${doctor._id}/organisation`, {
        affiliation,
        ...(isAssaya && { phcName: phcName.trim() }),
      });
      onSaved(response?.data?.data || {});
      toast.success("Organisation updated");
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Could not update the organisation"
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card sx={{ borderRadius: "18px", p: 3 }} data-testid="doctor-organisation">
      <Typography variant="h6" fontWeight={700}>
        Organisation
      </Typography>
      <Typography fontSize="13px" color="#6b7280" mb={2}>
        Set here only; the doctor sees it read-only. It applies to the doctor and
        their staff from their next sign-in or reload.
      </Typography>
      <Divider sx={{ mb: 3 }} />
      <Box display="flex" gap={2} flexWrap="wrap" alignItems="flex-start">
        <TextField
          select
          size="small"
          label="Affiliation"
          value={affiliation}
          onChange={(e) => setAffiliation(e.target.value)}
          sx={{ minWidth: 220 }}
          inputProps={{ "data-testid": "organisation-affiliation" }}
        >
          {AFFILIATIONS.map((value) => (
            <MenuItem key={value} value={value}>
              {value}
            </MenuItem>
          ))}
        </TextField>
        {isAssaya && (
          <TextField
            size="small"
            label="PHC Name"
            value={phcName}
            onChange={(e) => setPhcName(e.target.value)}
            inputProps={{ maxLength: 200 }}
            sx={{ minWidth: 260 }}
          />
        )}
        <Button
          variant="contained"
          onClick={save}
          disabled={!changed || saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
        >
          Save
        </Button>
      </Box>
    </Card>
  );
};

export default DoctorOrganisationCard;
